import { useCallback, useEffect, useState } from "react";
import { CAMPO, Recado } from "./ui";
import Carregando from "./Carregando";
import {
  lerCronograma,
  salvarCronograma,
  type CronogramaDaTurma,
  type ItemDoCronograma,
  type RespostaDoCronograma,
} from "../lib/adminPos";
import {
  casarComAGrade,
  datasQueNaoConferem,
  lerCronogramaColado,
} from "../lib/cronogramaColado";

/**
 * Admin › Faculdade › Turmas › **o cronograma de liberação** (000272).
 *
 * ---------------------------------------------------------------------------
 * A DATA não se digita, e não se guarda
 * ---------------------------------------------------------------------------
 * O que se edita é o INÍCIO da turma e o PRAZO em dias de cada disciplina. A
 * data de liberação é derivada no banco (`inicio_em + prazo_dias`) e volta
 * pronta — esta tela não calcula nenhuma.
 *
 * Isso não é purismo: medido no cronograma real da Pós Perícia, a data escrita
 * no documento é `início + prazo` nas 25 de 25 linhas. Guardar o que se deriva é
 * a cicatriz do teto do MEI, que o Bubble estampava e que por isso continuava
 * mostrando o proporcional de 2023 em 2026. Aqui, atrasar a turma um dia move as
 * 25 datas sozinho — no Word, alguém reescreve 25 células.
 *
 * ---------------------------------------------------------------------------
 * Colar a tabela é o caminho, e a REGRA dela mora fora daqui
 * ---------------------------------------------------------------------------
 * O cronograma de cada produto é um `.docx` no Drive, e o hub não tem
 * credencial do Google. O operador abre o documento, seleciona a tabela, copia
 * e cola; quem interpreta é `lib/cronogramaColado.ts`, módulo puro com prova
 * própria (23 asserções sobre o documento REAL).
 *
 * O que se cola NÃO é aplicado direto: ele preenche os campos, e o operador
 * confere antes de salvar. Entre colar e gravar existe o ensaio do banco.
 *
 * ---------------------------------------------------------------------------
 * O que esta tela NÃO faz
 * ---------------------------------------------------------------------------
 * Ela não muda a GRADE — nem quais disciplinas, nem a ordem delas. Quem faz
 * isso é o editor de grade, ao lado, e são duas perguntas diferentes: a grade
 * diz QUAIS disciplinas a turma cursa, o cronograma diz QUANDO cada uma abre.
 *
 * E ela não esconde nada do aluno. A liberação efetiva alcançaria 43.935
 * matrículas e não foi pedida: o pedido é um painel para o time acompanhar.
 * Isso está DITO na tela, porque um painel que mostra "liberada" sem o aluno
 * ver diferença é uma promessa que ninguém fez.
 */

type Rascunho = {
  inicio: string;
  /** Por `disciplina_id`: o que está nos campos, como texto. */
  prazos: Record<string, string>;
  modulos: Record<string, string>;
  motivo: string;
};

function doCronograma(itens: ItemDoCronograma[], inicio: string | null): Rascunho {
  return {
    inicio: inicio ?? "",
    prazos: Object.fromEntries(
      itens.map((i) => [i.disciplina_id, i.prazo_dias === null ? "" : String(i.prazo_dias)]),
    ),
    modulos: Object.fromEntries(
      itens.map((i) => [i.disciplina_id, i.modulo === null ? "" : String(i.modulo)]),
    ),
    motivo: "",
  };
}

/** `2026-07-20` → `20/07/2026`, por SPLIT.
 *
 * Nunca `new Date(iso)`: data SEM hora é lida como meia-noite em UTC, que em
 * -03:00 é o DIA ANTERIOR — a cicatriz do prazo da Pós RT aparecendo um dia
 * antes na tela de produtos, e a do fato gerador das Calculadoras. */
function dataBr(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

export default function AdminPosCronograma({
  curso,
  turma,
  aoSalvar,
}: {
  curso: string;
  turma: string;
  /** Recarrega a lista de turmas do pai — o início mudou. */
  aoSalvar: () => Promise<void>;
}) {
  const [dados, setDados] = useState<CronogramaDaTurma | null>(null);
  const [form, setForm] = useState<Rascunho | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [resposta, setResposta] = useState<RespostaDoCronograma | null>(null);
  const [colando, setColando] = useState(false);
  const [colado, setColado] = useState("");
  const [recadoDaColagem, setRecadoDaColagem] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const c = await lerCronograma(curso, turma);
    setDados(c);
    if (c?.ok && c.itens) setForm(doCronograma(c.itens, c.turma?.inicio_em ?? null));
  }, [curso, turma]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!dados) return <div className="border-t border-borda p-4"><Carregando /></div>;

  if (!dados.ok) {
    return (
      <div className="border-t border-borda p-4" data-cronograma-recusado>
        <Recado tipo="erro" texto={dados.detalhe ?? `Não deu para ler o cronograma (${dados.motivo}).`} />
      </div>
    );
  }

  const itens = dados.itens ?? [];
  const resumo = dados.resumo;

  if (!form) return <div className="border-t border-borda p-4"><Carregando /></div>;

  /** Lê o que foi colado e PREENCHE os campos — não salva nada. */
  function aplicarColagem() {
    const linhas = lerCronogramaColado(colado);
    if (linhas.length === 0) {
      setRecadoDaColagem(
        "Não reconheci nenhuma linha. Cole a tabela inteira do documento, com as colunas — " +
          "o navegador separa as células sozinho.",
      );
      return;
    }
    const casado = casarComAGrade(
      linhas,
      itens.map((i) => ({ disciplina_id: i.disciplina_id, nome: i.nome })),
    );

    const prazos = { ...form!.prazos };
    const modulos = { ...form!.modulos };
    for (const c of casado.casaram) {
      prazos[c.disciplina_id] = c.prazo_dias === null ? "" : String(c.prazo_dias);
      modulos[c.disciplina_id] = c.modulo === null ? "" : String(c.modulo);
    }
    setForm({ ...form!, prazos, modulos });

    // As três contagens são ditas juntas, e as duas últimas com NOME: "18 de 25
    // casaram" sem dizer QUAIS são as 7 é um número que não muda conduta.
    const partes = [`${casado.casaram.length} de ${linhas.length} linha(s) casaram com a grade.`];
    if (casado.sem_grade.length > 0) {
      partes.push(
        `Não estão na grade desta turma: ${casado.sem_grade.map((l) => l.nome).join(", ")}. ` +
          "Ponha-as na grade primeiro, no editor ao lado.",
      );
    }
    if (casado.sem_documento.length > 0) {
      partes.push(
        `Estão na grade e não no documento: ${casado.sem_documento.map((d) => d.nome).join(", ")}. ` +
          "Elas ficam sem prazo.",
      );
    }
    // A data do documento não vai para o banco, mas se ela DISCORDA de
    // início + prazo alguém editou o Word à mão — e isso precisa de gente.
    const fora = datasQueNaoConferem(form!.inicio || null, linhas);
    if (fora.length > 0) {
      partes.push(
        `Atenção: ${fora.length} data(s) escritas no documento não batem com início + prazo ` +
          `(ex.: ${fora[0].nome} está escrita ${dataBr(fora[0].escrita)} e calcula ${dataBr(fora[0].calculada)}). ` +
          "O hub usa o PRAZO; confira o documento.",
      );
    }
    setRecadoDaColagem(partes.join(" "));
    setColando(false);
  }

  async function salvar(confirmar: boolean) {
    setSalvando(true);
    const r = await salvarCronograma(
      turma,
      form!.inicio.trim() === "" ? null : form!.inicio,
      itens.map((i) => ({
        disciplina_id: i.disciplina_id,
        prazo_dias: form!.prazos[i.disciplina_id]?.trim() === "" ? null : Number(form!.prazos[i.disciplina_id]),
        modulo: form!.modulos[i.disciplina_id]?.trim() === "" ? null : Number(form!.modulos[i.disciplina_id]),
      })),
      form!.motivo,
      confirmar,
    );
    setSalvando(false);
    setResposta(r);
    if (r.ok) {
      await carregar();
      await aoSalvar();
    }
  }

  return (
    <div className="border-t border-borda p-4 sm:p-5" data-cronograma={turma}>
      {/* ── O resumo, derivado no banco ───────────────────────────────────── */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <h4 className="font-display text-base font-bold text-titulo">Cronograma de liberação</h4>
        {resumo && (
          <p className="text-sm text-cinza" data-resumo-cronograma>
            {resumo.com_prazo === 0 ? (
              "Nenhuma disciplina tem prazo ainda."
            ) : (
              <>
                {resumo.liberadas} liberada(s) · {resumo.faltam} a liberar
                {resumo.proxima_em && ` · próxima em ${dataBr(resumo.proxima_em)}`}
                {resumo.sem_prazo > 0 && ` · ${resumo.sem_prazo} sem prazo`}
              </>
            )}
          </p>
        )}
      </div>

      {/* O que o painel NÃO faz, dito onde alguém poderia supor o contrário.
          "Liberada" aqui é acompanhamento do time; o aluno continua vendo a
          grade inteira desde o primeiro dia. */}
      <p className="mt-1 text-xs text-cinza" data-nao-esconde>
        "Liberada" é o acompanhamento do time. O hub ainda não esconde do aluno a disciplina que
        não chegou a data — isso alcançaria as matrículas todas e não foi pedido.
      </p>

      {resumo?.ordem_diverge_da_grade && (
        <p className="mt-3 rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-3 text-sm text-alerta-tinta"
           data-ordem-diverge>
          A ordem da grade não é a ordem de liberação: alguma disciplina abre antes de outra que vem
          antes dela na grade. Nenhuma das duas foi mexida — a grade é o que o aluno vê, e quem
          decide qual está certa é gente.
        </p>
      )}

      {/* ── O início da turma: a âncora de tudo ───────────────────────────── */}
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Início da turma</span>
          <input
            type="date"
            data-campo="inicio"
            className={CAMPO}
            value={form.inicio}
            onChange={(e) => setForm({ ...form, inicio: e.target.value })}
          />
          <span className="text-xs text-cinza">
            Todas as datas saem daqui. Mudar este campo move o cronograma inteiro.
          </span>
        </label>

        <button
          type="button"
          data-abrir-colagem
          onClick={() => setColando((v) => !v)}
          className="btn-contorno min-h-10 text-sm"
        >
          {colando ? "Fechar" : "Colar do documento"}
        </button>
      </div>

      {colando && (
        <div className="mt-4 rounded-cf-lg border border-borda p-4" data-caixa-colagem>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Cole a tabela do cronograma</span>
            <textarea
              data-campo="colado"
              className={`${CAMPO} min-h-32 font-mono text-xs`}
              value={colado}
              onChange={(e) => setColado(e.target.value)}
              placeholder={"1\t0 (já liberada)\t20/07/2026\tMódulo 1\tDisciplina I – O que é Perícia"}
            />
            <span className="text-xs text-cinza">
              Abra o documento no Drive, selecione a tabela inteira e cole aqui. Isto PREENCHE os
              campos abaixo — nada é salvo até você confirmar.
            </span>
          </label>
          <button
            type="button"
            data-aplicar-colagem
            onClick={aplicarColagem}
            className="btn-contorno mt-3 min-h-10 text-sm"
          >
            Preencher os campos
          </button>
        </div>
      )}

      {recadoDaColagem && (
        <p className="mt-3 text-sm text-cinza" data-recado-colagem>
          {recadoDaColagem}
        </p>
      )}

      {/* ── A tabela ──────────────────────────────────────────────────────── */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borda text-left text-xs uppercase text-cinza">
              <th className="py-2 pr-3 font-semibold">Disciplina</th>
              <th className="py-2 pr-3 font-semibold">Módulo</th>
              <th className="py-2 pr-3 font-semibold">Prazo (dias)</th>
              <th className="py-2 pr-3 font-semibold">Libera em</th>
              <th className="py-2 font-semibold">Situação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {itens.map((i) => (
              <tr key={i.disciplina_id} data-linha-cronograma={i.disciplina_id}>
                <td className="py-2 pr-3 text-tinta">{i.nome}</td>
                <td className="py-2 pr-3">
                  <input
                    data-campo-modulo={i.disciplina_id}
                    className={`${CAMPO} w-16`}
                    inputMode="numeric"
                    value={form.modulos[i.disciplina_id] ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        modulos: {
                          ...form.modulos,
                          [i.disciplina_id]: e.target.value.replace(/\D/g, "").slice(0, 2),
                        },
                      })
                    }
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    data-campo-prazo={i.disciplina_id}
                    className={`${CAMPO} w-20`}
                    inputMode="numeric"
                    value={form.prazos[i.disciplina_id] ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        prazos: {
                          ...form.prazos,
                          [i.disciplina_id]: e.target.value.replace(/\D/g, "").slice(0, 4),
                        },
                      })
                    }
                  />
                </td>
                {/* A data vem DO BANCO, derivada. Ela não acompanha o que se
                    digita — e isso é o certo: o que está na tela é o que está
                    gravado, e o resto aparece depois de salvar. */}
                <td className="py-2 pr-3 text-cinza" data-libera-em={i.disciplina_id}>
                  {dataBr(i.libera_em)}
                </td>
                <td className="py-2">
                  {i.liberada === null ? (
                    <span className="text-xs text-cinza">sem data</span>
                  ) : i.liberada ? (
                    <span className="rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-semibold text-aviso-tinta">
                      liberada
                    </span>
                  ) : (
                    <span className="rounded-full bg-chip px-2 py-0.5 text-xs text-chip-tinta">
                      a liberar
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {itens.length === 0 && (
        <p className="mt-3 text-sm text-alerta-tinta" data-grade-vazia>
          Esta turma não tem grade. O cronograma diz QUANDO cada disciplina abre — quais são elas é
          o editor de grade, ao lado.
        </p>
      )}

      {/* ── O motivo e o salvar ───────────────────────────────────────────── */}
      {itens.length > 0 && (
        <>
          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
            <input
              data-campo="motivo"
              className={CAMPO}
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              placeholder="ex.: cronograma da turma 7, conforme o documento do Drive"
            />
            <span className="text-xs text-cinza">Vai para o razão, com o antes e o depois.</span>
          </label>

          <div className="mt-4 flex flex-wrap gap-3">
            {/* SEMPRE ensaia antes: a data que sai daqui é a que a equipe vai
                anunciar ao aluno. Quem conta quantas mudam e quantos alunos
                estão na turma é o banco, e não esta tela. */}
            <button
              type="button"
              data-ensaiar-cronograma
              onClick={() => void salvar(false)}
              disabled={salvando}
              className="btn-contorno min-h-12 disabled:opacity-55"
            >
              {salvando ? "Conferindo…" : "Conferir o que muda"}
            </button>
            {resposta?.motivo === "ensaio" && (
              <button
                type="button"
                data-salvar-cronograma
                onClick={() => void salvar(true)}
                disabled={salvando}
                className="pill-cta min-h-12 px-6 disabled:opacity-55"
              >
                {salvando ? "Salvando…" : "Confirmar e salvar"}
              </button>
            )}
          </div>

          {resposta && (
            <div className="mt-3">
              <Recado
                tipo={resposta.ok ? "ok" : resposta.motivo === "ensaio" ? "ok" : "erro"}
                texto={resposta.detalhe ?? (resposta.ok ? "Salvo." : `Recusado (${resposta.motivo}).`)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
